/**
 * Business templates.
 *
 * A template is **data, not code**. It says what a new business of a given
 * type should start with — catalogue categories, expense categories, which
 * sales channels to switch on — and what its catalogue should be *called*.
 *
 * That last part matters more than it looks. A restaurant owner does not have
 * "products", they have menu items; a salon has services; a pharmacy has
 * medicines. Software that insists on its own noun makes every screen feel
 * like it was built for somebody else's business, and the fix costs nothing
 * but a label.
 *
 * ── Why this is not `industry/registry.ts` ───────────────────────────────────
 *
 * That seam is reserved for Phase 3 industry *modules*: code that adds tables,
 * routes and migrations (a restaurant's kitchen queue, a logistics rider
 * settlement ledger). Templates add no tables and no endpoints. Keeping the
 * two apart means the nine templates below cost fifteen lines of data each and
 * cannot introduce a bug, while the heavier seam stays unused until something
 * genuinely needs it.
 *
 * A template is applied once, at onboarding. It seeds rows the owner then owns
 * outright — they can rename or delete every one of them. Nothing downstream
 * reads the template again, so a business is never locked into the shape it
 * was created with.
 */

import {
  BUSINESS_TYPES,
  SELECTABLE_BUSINESS_TYPES,
  type BusinessType,
  type OfferingKind,
} from './enums.js';

export interface BusinessTemplate {
  type: BusinessType;
  /** What this business calls the things it sells, singular and plural. */
  catalogueNoun: { singular: string; plural: string };
  /** The catalogue's opening categories. Renameable and deletable. */
  productCategories: readonly string[];
  /** Added to DEFAULT_EXPENSE_CATEGORIES for this type. */
  extraExpenseCategories: readonly string[];
  /** What this type of business usually sells, pre-selected at onboarding. */
  defaultOffering: OfferingKind;
  /**
   * Whether a typical business of this type counts stock. Pre-selects
   * `trackStock` on new catalogue entries; always editable per item.
   */
  tracksStockByDefault: boolean;
  /** One line shown under the type in the onboarding picker. */
  hint: string;
}

const TEMPLATES: Record<BusinessType, BusinessTemplate> = {
  restaurant: {
    type: 'restaurant',
    catalogueNoun: { singular: 'Menu item', plural: 'Menu' },
    productCategories: ['Main dishes', 'Sides', 'Drinks', 'Desserts', 'Combos'],
    extraExpenseCategories: ['Ingredients & Produce', 'Gas & Cooking Fuel', 'Packaging'],
    defaultOffering: 'both',
    // A kitchen counts ingredients, not plated dishes, and ingredient-level
    // stock is a Phase 3 concern. Starting with tracking off avoids every menu
    // item immediately reading "out of stock".
    tracksStockByDefault: false,
    hint: 'Dine-in, takeaway or catering',
  },

  water_production: {
    type: 'water_production',
    catalogueNoun: { singular: 'Product', plural: 'Products' },
    productCategories: ['Sachet water', 'Bottled water', 'Dispenser bottles', 'Branded runs'],
    extraExpenseCategories: ['Treatment Chemicals', 'Nylon & Packaging', 'Distribution'],
    defaultOffering: 'products',
    tracksStockByDefault: true,
    hint: 'Production and distribution',
  },

  supermarket_retail: {
    type: 'supermarket_retail',
    catalogueNoun: { singular: 'Product', plural: 'Products' },
    productCategories: [
      'Groceries',
      'Beverages',
      'Household',
      'Personal care',
      'Frozen',
      'Snacks',
    ],
    extraExpenseCategories: ['Stock Purchases', 'Cold Storage', 'Shop Fittings'],
    defaultOffering: 'products',
    tracksStockByDefault: true,
    hint: 'Shop, minimart or supermarket',
  },

  logistics: {
    type: 'logistics',
    catalogueNoun: { singular: 'Service', plural: 'Services' },
    productCategories: ['Local delivery', 'Interstate', 'Same-day', 'Bulk haulage'],
    extraExpenseCategories: ['Rider Commission', 'Vehicle Maintenance', 'Tolls & Permits'],
    defaultOffering: 'services',
    tracksStockByDefault: false,
    hint: 'Delivery, haulage or courier',
  },

  hotel: {
    type: 'hotel',
    catalogueNoun: { singular: 'Room or service', plural: 'Rooms & services' },
    productCategories: ['Rooms', 'Food & drink', 'Laundry', 'Events'],
    extraExpenseCategories: ['Housekeeping Supplies', 'Linen & Laundry', 'Guest Amenities'],
    defaultOffering: 'both',
    tracksStockByDefault: false,
    hint: 'Hotel, guesthouse or short-let',
  },

  salon: {
    type: 'salon',
    catalogueNoun: { singular: 'Service', plural: 'Services' },
    productCategories: ['Hair', 'Nails', 'Skincare', 'Retail products'],
    extraExpenseCategories: ['Salon Products', 'Stylist Commission', 'Equipment'],
    defaultOffering: 'both',
    tracksStockByDefault: false,
    hint: 'Salon, barbershop or spa',
  },

  professional_services: {
    type: 'professional_services',
    catalogueNoun: { singular: 'Service', plural: 'Services' },
    productCategories: ['Consultation', 'Project work', 'Retainer'],
    extraExpenseCategories: ['Professional Fees', 'Software & Subscriptions', 'Office Costs'],
    defaultOffering: 'services',
    tracksStockByDefault: false,
    hint: 'Consulting, legal, accounting or agency work',
  },

  manufacturing: {
    type: 'manufacturing',
    catalogueNoun: { singular: 'Product', plural: 'Products' },
    productCategories: ['Finished goods', 'Raw materials', 'Packaging'],
    extraExpenseCategories: ['Raw Materials', 'Machine Maintenance', 'Factory Utilities'],
    defaultOffering: 'products',
    tracksStockByDefault: true,
    hint: 'Making goods to sell or supply',
  },

  healthcare: {
    type: 'healthcare',
    catalogueNoun: { singular: 'Service or item', plural: 'Services & items' },
    productCategories: ['Consultations', 'Procedures', 'Pharmacy'],
    extraExpenseCategories: ['Medical Supplies', 'Equipment Maintenance', 'Licences'],
    defaultOffering: 'both',
    tracksStockByDefault: false,
    hint: 'Clinic, pharmacy, lab or care service',
  },

  pharmacy: {
    type: 'pharmacy',
    catalogueNoun: { singular: 'Medicine or product', plural: 'Medicines & products' },
    productCategories: [
      'Prescription medicines',
      'Over the counter',
      'Cosmetics & personal care',
      'Baby care',
      'Medical devices',
      'General retail',
    ],
    // Electricity apart from Utilities: a pharmacy's power (grid, generator,
    // fuel for the cold chain) is its largest running cost after rent and wages.
    extraExpenseCategories: ['Electricity', 'Administrative', 'Cold Chain', 'Regulatory Fees', 'Professional Licences'],
    defaultOffering: 'products',
    tracksStockByDefault: true,
    hint: 'Pharmacy or chemist — medicines, cosmetics and retail',
  },

  other: {
    type: 'other',
    catalogueNoun: { singular: 'Product or service', plural: 'Products & services' },
    productCategories: ['General'],
    extraExpenseCategories: [],
    defaultOffering: 'both',
    tracksStockByDefault: false,
    hint: 'Anything else — you can shape it yourself',
  },
  /**
   * A workspace with no shop in it.
   *
   * Seeds nothing, deliberately. This template exists so the type system has
   * an entry, not so a platform workspace can be furnished — CenoX Business
   * activation is what hands a workspace a shop's categories and channels.
   */
  platform: {
    type: 'platform',
    catalogueNoun: { singular: 'Item', plural: 'Items' },
    productCategories: [],
    extraExpenseCategories: [],
    defaultOffering: 'both',
    tracksStockByDefault: false,
    hint: 'A CenoX workspace with no shop in it',
  },
};

/**
 * Templates keyed by a business type that does not have its own entry yet.
 *
 * Clothing, pharmacy, electronics and professional services are named in the
 * roadmap but are not `BusinessType` values, because that enum is persisted in
 * Postgres and adding a value needs a migration. They are listed here as
 * *presets* over the `other` type so onboarding can offer them today, and can
 * be promoted to first-class types later without the data moving.
 */
export interface BusinessPreset {
  slug: string;
  name: string;
  /** The persisted type this preset stores as. */
  type: BusinessType;
  template: Omit<BusinessTemplate, 'type'>;
}

export const BUSINESS_PRESETS: readonly BusinessPreset[] = [
  {
    slug: 'clothing_fashion',
    name: 'Clothing & Fashion',
    type: 'supermarket_retail',
    template: {
      catalogueNoun: { singular: 'Item', plural: 'Items' },
      productCategories: ['Menswear', 'Womenswear', 'Children', 'Footwear', 'Accessories'],
      extraExpenseCategories: ['Stock Purchases', 'Tailoring', 'Shop Fittings'],
      defaultOffering: 'products',
      tracksStockByDefault: true,
      hint: 'Boutique, thrift or fashion label',
    },
  },
  {
    slug: 'pharmacy',
    name: 'Pharmacy',
    type: 'supermarket_retail',
    template: {
      catalogueNoun: { singular: 'Medicine', plural: 'Medicines' },
      productCategories: ['Prescription', 'Over the counter', 'Personal care', 'Baby care'],
      extraExpenseCategories: ['Stock Purchases', 'Cold Chain', 'Regulatory Fees'],
      defaultOffering: 'both',
      tracksStockByDefault: true,
      hint: 'Pharmacy or chemist',
    },
  },
  {
    slug: 'electronics',
    name: 'Electronics',
    type: 'supermarket_retail',
    template: {
      catalogueNoun: { singular: 'Product', plural: 'Products' },
      productCategories: ['Phones', 'Computers', 'Accessories', 'Home appliances', 'Repairs'],
      extraExpenseCategories: ['Stock Purchases', 'Warranty & Repairs', 'Shop Fittings'],
      defaultOffering: 'both',
      tracksStockByDefault: true,
      hint: 'Phones, computers or appliances',
    },
  },
  {
    slug: 'professional_services',
    name: 'Professional Services',
    type: 'other',
    template: {
      catalogueNoun: { singular: 'Service', plural: 'Services' },
      productCategories: ['Consulting', 'Retainers', 'Projects', 'Training'],
      extraExpenseCategories: ['Software & Subscriptions', 'Professional Fees', 'Travel'],
      defaultOffering: 'services',
      tracksStockByDefault: false,
      hint: 'Consulting, legal, accounting, agency',
    },
  },
];

/** The template for a business type. Never throws — `other` is the fallback. */
export function templateFor(type: BusinessType): BusinessTemplate {
  return TEMPLATES[type] ?? TEMPLATES.other;
}

/** The template for a preset slug, falling back to the plain type template. */
export function templateForPreset(
  type: BusinessType,
  presetSlug?: string | null,
): BusinessTemplate {
  if (!presetSlug) return templateFor(type);
  const preset = BUSINESS_PRESETS.find((p) => p.slug === presetSlug);
  return preset ? { type, ...preset.template } : templateFor(type);
}

/** Everything onboarding offers, plain types and presets together. */
export function onboardingChoices(): Array<{
  value: string;
  label: string;
  type: BusinessType;
  presetSlug: string | null;
  hint: string;
}> {
  // `other` is offered separately, at the end. `platform` is never offered —
  // it is what a workspace is when nobody has told it it is a shop.
  const fromTypes = SELECTABLE_BUSINESS_TYPES.filter((type) => type !== 'other').map((type) => ({
    value: type,
    label: LABELS[type],
    type,
    presetSlug: null,
    hint: templateFor(type).hint,
  }));

  // The `pharmacy` preset is a type of its own now. It stays in the list so a
  // business created from it keeps its terminology, but it is not offered
  // twice.
  const fromPresets = BUSINESS_PRESETS.filter(
    (preset) => !(SELECTABLE_BUSINESS_TYPES as readonly string[]).includes(preset.slug),
  ).map((preset) => ({
    value: `preset:${preset.slug}`,
    label: preset.name,
    type: preset.type,
    presetSlug: preset.slug,
    hint: preset.template.hint,
  }));

  return [
    ...fromTypes,
    ...fromPresets,
    {
      value: 'other',
      label: 'Other',
      type: 'other' as BusinessType,
      presetSlug: null,
      hint: templateFor('other').hint,
    },
  ];
}

/** Imported lazily to keep this module free of a cycle through enums' labels. */
const LABELS: Record<BusinessType, string> = {
  restaurant: 'Restaurant',
  water_production: 'Water Production',
  supermarket_retail: 'Supermarket / Retail',
  logistics: 'Delivery & Logistics',
  hotel: 'Hotel',
  salon: 'Salon',
  professional_services: 'Professional Services',
  manufacturing: 'Manufacturing',
  healthcare: 'Healthcare',
  other: 'Other',
  platform: 'CenoX Workspace',
  pharmacy: 'Pharmacy',
};
